import { API_BASE, SUPER_ADMIN_EMAIL, getCsrfToken } from '../../config';
// components/Announcements/AnnouncementsManager.js
import React, { useState, useEffect } from 'react';
import { Home, Megaphone } from 'lucide-react';
import { useTranslation } from '../../i18n/LanguageContext';
import { useAuth } from '../../App';

// Hook personnalisé pour les API des communiqués
const useAnnouncements = () => {
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };


  const getAnnouncements = async (filters = {}) => {
    try {
      const params = new URLSearchParams(filters);
      const response = await fetch(`${API_BASE}/api/announcements?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur récupération annonces:', error);
      return { error: 'Connection error' };
    }
  };

  const createAnnouncement = async (announcementData) => {
    try {
      const csrf = await getCsrfToken();
      if (!csrf) return { error: 'Please refresh the page and try again.' };
      const response = await fetch(`${API_BASE}/api/announcements`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf },
        body: JSON.stringify(announcementData)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur création annonce:', error);
      return { error: error.message || 'Connection error' };
    }
  };

  const updateAnnouncement = async (id, announcementData) => {
    try {
      const csrf = await getCsrfToken();
      if (!csrf) return { error: 'Please refresh the page and try again.' };
      const response = await fetch(`${API_BASE}/api/announcements/${id}`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf },
        body: JSON.stringify(announcementData)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur mise à jour annonce:', error);
      return { error: 'Connection error' };
    }
  };

  const deleteAnnouncement = async (id) => {
    try {
      const csrf = await getCsrfToken();
      if (!csrf) return { error: 'Please refresh the page and try again.' };
      const response = await fetch(`${API_BASE}/api/announcements/${id}`, {
        method: 'DELETE',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur suppression annonce:', error);
      return { error: 'Connection error' };
    }
  };

  const markAsRead = async (id) => {
    try {
      const csrf = await getCsrfToken();
      if (!csrf) return { error: 'Please refresh the page and try again.' };
      const response = await fetch(`${API_BASE}/api/announcements/${id}/mark-read`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'X-CSRF-Token': csrf }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur marquage lu:', error);
      return { error: 'Connection error' };
    }
  };

  const getUnreadAnnouncements = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/announcements/unread`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur récupération non lus:', error);
      return { error: 'Connection error' };
    }
  };

  const getAnnouncementStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/announcements/stats`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('❌ Erreur récupération stats:', error);
      return { error: 'Connection error' };
    }
  };

  return { 
    getAnnouncements, 
    createAnnouncement, 
    updateAnnouncement, 
    deleteAnnouncement, 
    markAsRead, 
    getUnreadAnnouncements, 
    getAnnouncementStats 
  };
};

// Composant Widget des communiqués non lus - Version mobile responsive
const UnreadAnnouncementsWidget = () => {
  const { t } = useTranslation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [recentAnnouncements, setRecentAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWidget, setShowWidget] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const { getUnreadAnnouncements, markAsRead } = useAnnouncements();

  // Détecter si on est sur mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const loadUnreadAnnouncements = async () => {
    const result = await getUnreadAnnouncements();
    if (!result.error && result.success) {
      setUnreadCount(result.count);
      setRecentAnnouncements(result.announcements.slice(0, 3));
    }
    setLoading(false);
  };

  useEffect(() => {
    loadUnreadAnnouncements();
    const interval = setInterval(loadUnreadAnnouncements, 30000); // Actualiser toutes les 30s
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- poll uses current loadUnreadAnnouncements; adding it would reset interval every render
  }, []);

  const handleMarkAsRead = async (id) => {
    const result = await markAsRead(id);
    if (!result.error && result.success) {
      loadUnreadAnnouncements();
    }
  };

  if (loading || !showWidget || unreadCount === 0) return null;

  const widgetStyles = {
    position: 'fixed',
    top: isMobile ? '10px' : '20px',
    right: isMobile ? '10px' : '20px',
    left: isMobile ? '10px' : 'auto',
    background: 'linear-gradient(45deg, #ff6b6b, #ff5252)',
    color: 'white',
    padding: isMobile ? '15px' : '20px',
    borderRadius: '15px',
    maxWidth: isMobile ? 'calc(100vw - 20px)' : '350px',
    zIndex: 1000,
    boxShadow: '0 8px 32px rgba(255, 107, 107, 0.4)',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    animation: 'slideInFromRight 0.5s ease-out',
    fontSize: isMobile ? '0.9rem' : '1rem'
  };

  return (
    <div style={widgetStyles}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '15px',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: isMobile ? '10px' : '0'
      }}>
        <h4 style={{ 
          margin: 0, 
          fontSize: isMobile ? '14px' : '16px', 
          fontWeight: 'bold',
          flex: isMobile ? '1 1 100%' : 'auto',
          textAlign: isMobile ? 'center' : 'left'
        }}>
          🔔 {unreadCount} {unreadCount > 1 ? t('messages.unread_count_plural') : t('messages.unread_count')} {unreadCount > 1 ? t('messages.announcement_plural') : t('messages.announcement')}
        </h4>
        <button
          onClick={() => setShowWidget(false)}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            color: 'white',
            cursor: 'pointer',
            fontSize: '18px',
            borderRadius: '50%',
            width: '28px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0
          }}
        >
          ✕
        </button>
      </div>
      
      <div style={{ 
        fontSize: isMobile ? '12px' : '13px', 
        opacity: 0.9, 
        marginBottom: '15px',
        textAlign: isMobile ? 'center' : 'left'
      }}>
        {t('messages.important_messages')}
      </div>
      
      {recentAnnouncements.map(announcement => (
        <div key={announcement.id} style={{
          background: 'rgba(0,0,0,0.2)',
          padding: isMobile ? '8px' : '10px',
          borderRadius: '8px',
          marginBottom: '10px',
          fontSize: isMobile ? '11px' : '12px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
            {announcement.priority === 'urgent' && '🚨 '}
            {announcement.title}
          </div>
          <div style={{ opacity: 0.8, marginBottom: '8px' }}>
            {t('messages.by')} {announcement.author_name}
          </div>
          <button
            onClick={() => handleMarkAsRead(announcement.id)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              color: 'white',
              padding: '4px 10px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: isMobile ? '9px' : '10px',
              fontWeight: 'bold',
              width: isMobile ? '100%' : 'auto'
            }}
          >
            ✅ {t('messages.mark_as_read')}
          </button>
        </div>
      ))}
      
      <button
        onClick={() => setShowWidget(false)}
        style={{
          background: 'rgba(255,255,255,0.9)',
          color: '#ff6b6b',
          border: 'none',
          padding: isMobile ? '12px 20px' : '10px 20px',
          borderRadius: '8px',
          cursor: 'pointer',
          fontSize: isMobile ? '11px' : '12px',
          fontWeight: 'bold',
          width: '100%',
          marginTop: '10px'
        }}
      >
        📢 {t('messages.see_all')}
      </button>
    </div>
  );
};

// Composant principal de gestion des communiqués - Version mobile responsive
const AnnouncementsManager = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const canManageAnnouncements = user && (
    ['admin', 'manager', 'super_admin'].includes(user.role) ||
    (user.email || '').toLowerCase() === SUPER_ADMIN_EMAIL
  );
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [message, setMessage] = useState('');
  const [stats, setStats] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'info',
    priority: 'normal',
    targetAudience: 'all',
    isPinned: false,
    validUntil: ''
  });

  const { 
    getAnnouncements, 
    createAnnouncement, 
    updateAnnouncement, 
    deleteAnnouncement, 
    markAsRead, 
    getAnnouncementStats 
  } = useAnnouncements();

  const getAnnouncementTypes = () => [
    { value: 'info', label: t('messages.type_info'), color: '#2196F3' },
    { value: 'warning', label: t('messages.type_warning'), color: '#FF9800' },
    { value: 'success', label: t('messages.type_success'), color: '#4CAF50' },
    { value: 'urgent', label: t('messages.type_urgent'), color: '#f44336' },
    { value: 'maintenance', label: t('messages.type_maintenance'), color: '#607D8B' },
    { value: 'event', label: t('messages.type_event'), color: '#9C27B0' }
  ];

  const getPriorityOptions = () => [
    { value: 'low', label: t('messages.priority_low'), color: '#9E9E9E' },
    { value: 'normal', label: t('messages.priority_normal'), color: '#2196F3' },
    { value: 'high', label: t('messages.priority_high'), color: '#FF9800' },
    { value: 'urgent', label: t('messages.priority_urgent'), color: '#f44336' }
  ];
  
  const announcementTypes = getAnnouncementTypes();
  const priorityOptions = getPriorityOptions();

  // Détecter si on est sur mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const loadAnnouncements = async () => {
    try {
      const result = await getAnnouncements();
      if (result.success) {
        setAnnouncements(result.announcements);
      } else {
        setMessage(`❌ ${result.error === 'Connection error' ? t('messages.error_connection') : result.error}`);
      }
    } catch (error) {
      console.error('Erreur chargement communiqués:', error);
      setMessage('❌ ' + t('messages.error_loading'));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const result = await getAnnouncementStats();
      if (result.success) {
        setStats(result.stats);
      }
    } catch (error) {
      console.error('Erreur chargement statistiques:', error);
    }
  };

  useEffect(() => {
    loadAnnouncements();
    if (canManageAnnouncements) loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadAnnouncements/loadStats stable enough for mount + role change
  }, [canManageAnnouncements]);

  const handleSave = async () => {
    try {
      setLoading(true);
      
      let result;
      if (editingAnnouncement) {
        result = await updateAnnouncement(editingAnnouncement.id, formData);
      } else {
        result = await createAnnouncement(formData);
      }

      if (result.success) {
        setMessage(`✅ ${result.message}`);
        setShowCreateModal(false);
        setEditingAnnouncement(null);
        loadAnnouncements();
        loadStats();
        resetForm();
        setTimeout(() => setMessage(''), 5000);
      } else {
        setMessage(`❌ ${result.error === 'Connection error' ? t('messages.error_connection') : result.error}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      setMessage('❌ ' + t('messages.error_saving'));
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (announcementId) => {
    try {
      const result = await markAsRead(announcementId);
      if (result.success) {
        loadAnnouncements();
      }
    } catch (error) {
      console.error('Erreur marquage lecture:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('messages.delete_confirm'))) return;
    
    try {
      const result = await deleteAnnouncement(id);
      if (result.success) {
        setMessage(`✅ ${result.message}`);
        loadAnnouncements();
        loadStats();
        setTimeout(() => setMessage(''), 5000);
      } else {
        setMessage(`❌ ${result.error === 'Connection error' ? t('messages.error_connection') : result.error}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('Erreur suppression:', error);
      setMessage('❌ ' + t('messages.error_deleting'));
      setTimeout(() => setMessage(''), 5000);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      type: 'info',
      priority: 'normal',
      targetAudience: 'all',
      isPinned: false,
      validUntil: ''
    });
  };

  const openEditModal = (announcement) => {
    setEditingAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type,
      priority: announcement.priority,
      targetAudience: announcement.targetAudience || 'all',
      isPinned: announcement.isPinned,
      validUntil: announcement.validUntil ? announcement.validUntil.split('T')[0] : ''
    });
    setShowCreateModal(true);
  };

  const getTypeInfo = (type) => {
    return announcementTypes.find(t => t.value === type) || announcementTypes[0];
  };

  const getPriorityInfo = (priority) => {
    return priorityOptions.find(p => p.value === priority) || priorityOptions[1];
  };

  // Styles responsives
  const buttonStyles = {
    padding: isMobile ? '12px 20px' : '12px 24px',
    background: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: isMobile ? '13px' : '14px',
    width: isMobile ? '100%' : 'auto',
    maxWidth: isMobile ? '300px' : 'none'
  };

  const statsGridStyles = {
    display: 'grid',
    gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: isMobile ? '10px' : '15px',
    marginBottom: isMobile ? '20px' : '30px'
  };

  const statCardStyles = {
    background: 'rgba(33, 150, 243, 0.2)',
    border: '2px solid #2196F3',
    padding: isMobile ? '15px' : '20px',
    borderRadius: '12px',
    textAlign: 'center'
  };

  const announcementCardStyles = {
    background: 'rgba(255,255,255,0.15)',
    border: '2px solid #ff6b6b',
    borderRadius: '15px',
    padding: isMobile ? '15px' : '25px',
    marginBottom: isMobile ? '15px' : '20px',
    position: 'relative',
    transition: 'all 0.3s ease'
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        width: '100%',
        background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⏳</div>
          <div style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.8)' }}>
            {t('messages.loading')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      width: '100%',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      overflowX: 'hidden',
      paddingBottom: 'env(safe-area-inset-bottom, 0)',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflowY: 'auto'
    }}>
      {/* Header transparent */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '24px 20px 20px',
        background: 'transparent',
        backdropFilter: 'none',
        borderBottom: 'none',
        zIndex: 999,
        maxWidth: '100%'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '6px',
          gap: '16px'
        }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('home')}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '10px',
                borderRadius: '12px',
                fontSize: '18px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s',
                backdropFilter: 'blur(10px)',
                flexShrink: 0,
                width: '44px',
                height: '44px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                e.currentTarget.style.transform = 'scale(1)';
              }}
              title={t('messages.back_to_home')}
            >
              <Home size={20} />
            </button>
          )}
          <div style={{ 
            fontSize: 'clamp(28px, 5vw, 40px)', 
            fontWeight: 700, 
            maxWidth: '100%',
            letterSpacing: '-0.5px',
            lineHeight: 1.2,
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <Megaphone size={32} />
            {t('messages.title')}
          </div>
        </div>
        <p style={{
          fontSize: 'clamp(14px, 2vw, 16px)',
          color: 'rgba(255, 255, 255, 0.7)',
          margin: '8px 0 0 0',
          letterSpacing: '0.2px',
          fontWeight: 500
        }}>
          {t('messages.subtitle')}
        </p>
      </div>

      {/* Contenu principal */}
      <div className="announcements-content" style={{
        paddingTop: '140px',
        paddingBottom: '40px',
        paddingLeft: isMobile ? '10px' : '20px',
        paddingRight: isMobile ? '10px' : '20px',
        maxWidth: '1000px',
        margin: '0 auto'
      }}>
      {/* Widget des communiqués non lus */}
      <UnreadAnnouncementsWidget />

      {/* En-tête responsive */}
      <div style={{
        marginBottom: isMobile ? '15px' : '20px',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: isMobile ? 'center' : 'center',
        gap: isMobile ? '15px' : '0'
      }}>
        <h2 style={{
          color: '#ffffff',
          margin: 0,
          fontSize: isMobile ? '1.3rem' : '1.5rem',
          textAlign: isMobile ? 'center' : 'left',
          fontWeight: 700
        }}>📢 {t('messages.announcements_title')}</h2>
        {canManageAnnouncements && (
          <button
            onClick={() => {
              resetForm();
              setEditingAnnouncement(null);
              setShowCreateModal(true);
            }}
            style={{
              padding: isMobile ? '12px 20px' : '12px 24px',
              background: 'linear-gradient(135deg, #4CAF50, #45a049)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: isMobile ? '13px' : '14px',
              width: isMobile ? '100%' : 'auto',
              maxWidth: isMobile ? '300px' : 'none',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'translateY(-2px)';
              e.target.style.boxShadow = '0 6px 20px rgba(76, 175, 80, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(76, 175, 80, 0.3)';
            }}
          >
            ➕ {t('messages.new_announcement')}
          </button>
        )}
      </div>

      {/* Message responsive */}
      {message && (
        <div style={{
          background: message.includes('✅') ? 'rgba(76, 175, 80, 0.2)' : 'rgba(255, 107, 107, 0.2)',
          border: `2px solid ${message.includes('✅') ? '#4CAF50' : '#ff6b6b'}`,
          color: 'white',
          padding: isMobile ? '12px' : '15px',
          borderRadius: '8px',
          marginBottom: isMobile ? '15px' : '20px',
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: isMobile ? '13px' : '14px'
        }}>
          {message}
        </div>
      )}

      {/* Statistiques responsive */}
      {stats && (
        <div style={statsGridStyles}>
          <div style={{
            ...statCardStyles,
            background: 'rgba(33, 150, 243, 0.2)',
            borderColor: '#2196F3'
          }}>
            <div style={{ 
              color: '#2196F3', 
              fontSize: isMobile ? '20px' : '28px', 
              fontWeight: 'bold' 
            }}>
              {stats.total_announcements || 0}
            </div>
            <div style={{ 
              color: 'white', 
              fontSize: isMobile ? '11px' : '14px' 
            }}>
              {t('messages.total_announcements')}
            </div>
          </div>
          
          <div style={{
            ...statCardStyles,
            background: 'rgba(76, 175, 80, 0.2)',
            borderColor: '#4CAF50'
          }}>
            <div style={{ 
              color: '#4CAF50', 
              fontSize: isMobile ? '20px' : '28px', 
              fontWeight: 'bold' 
            }}>
              {stats.active_announcements || 0}
            </div>
            <div style={{ 
              color: 'white', 
              fontSize: isMobile ? '11px' : '14px' 
            }}>
              {t('messages.active')}
            </div>
          </div>
          
          <div style={{
            ...statCardStyles,
            background: 'rgba(255, 193, 7, 0.2)',
            borderColor: '#FFC107'
          }}>
            <div style={{ 
              color: '#FFC107', 
              fontSize: isMobile ? '20px' : '28px', 
              fontWeight: 'bold' 
            }}>
              {stats.pinned_announcements || 0}
            </div>
            <div style={{ 
              color: 'white', 
              fontSize: isMobile ? '11px' : '14px' 
            }}>
              {t('messages.pinned')}
            </div>
          </div>
          
          <div style={{
            ...statCardStyles,
            background: 'rgba(244, 67, 54, 0.2)',
            borderColor: '#f44336'
          }}>
            <div style={{ 
              color: '#f44336', 
              fontSize: isMobile ? '20px' : '28px', 
              fontWeight: 'bold' 
            }}>
              {stats.urgent_announcements || 0}
            </div>
            <div style={{ 
              color: 'white', 
              fontSize: isMobile ? '11px' : '14px' 
            }}>
              {t('messages.urgent')}
            </div>
          </div>
          
          {!isMobile && (
            <div style={{
              ...statCardStyles,
              background: 'rgba(156, 39, 176, 0.2)',
              borderColor: '#9C27B0'
            }}>
              <div style={{ 
                color: '#9C27B0', 
                fontSize: '28px', 
                fontWeight: 'bold' 
              }}>
                {stats.avg_read_rate || 0}%
              </div>
              <div style={{ 
                color: 'white', 
                fontSize: '14px' 
              }}>
                {t('messages.read_rate')}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Liste des communiqués responsive */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '15px' : '20px' }}>
        {announcements.length === 0 ? (
          <div style={{
            background: 'rgba(255,255,255,0.1)',
            padding: isMobile ? '30px 20px' : '40px',
            borderRadius: '15px',
            textAlign: 'center',
            color: '#9ca3af'
          }}>
            <h3 style={{ fontSize: isMobile ? '18px' : '24px' }}>{t('messages.no_announcements')}</h3>
            <p style={{ fontSize: isMobile ? '14px' : '16px' }}>
              {t('messages.no_announcements_desc')}
            </p>
            {canManageAnnouncements && (
              <button
                onClick={() => setShowCreateModal(true)}
                style={{
                  ...buttonStyles,
                  marginTop: '15px',
                  maxWidth: 'none'
                }}
              >
                ➕ {t('messages.create_first')}
              </button>
            )}
          </div>
        ) : (
          announcements
            .sort((a, b) => {
              // Naya create hua pehle (newest first), phir épinglé, phir urgent
              const dateA = new Date(a.createdAt).getTime();
              const dateB = new Date(b.createdAt).getTime();
              if (dateB !== dateA) return dateB - dateA; // newest at top
              if (a.isPinned && !b.isPinned) return -1;
              if (!a.isPinned && b.isPinned) return 1;
              if (a.priority === 'urgent' && b.priority !== 'urgent') return -1;
              if (a.priority !== 'urgent' && b.priority === 'urgent') return 1;
              return 0;
            })
            .map(announcement => {
              const typeInfo = getTypeInfo(announcement.type);
              const priorityInfo = getPriorityInfo(announcement.priority);
              
              return (
                <div
                  key={announcement.id}
                  style={{
                    ...announcementCardStyles,
                    background: announcement.readByCurrentUser 
                      ? 'rgba(255,255,255,0.1)' 
                      : 'rgba(255,255,255,0.15)',
                    border: announcement.isPinned 
                      ? '3px solid #FFD700' 
                      : announcement.readByCurrentUser 
                        ? '1px solid rgba(255,255,255,0.2)' 
                        : '2px solid #ff6b6b'
                  }}
                >
                  {/* En-tête responsive */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between', 
                    alignItems: isMobile ? 'stretch' : 'flex-start', 
                    marginBottom: '15px',
                    gap: isMobile ? '15px' : '0'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        marginBottom: '8px',
                        flexWrap: 'wrap',
                        justifyContent: isMobile ? 'center' : 'flex-start'
                      }}>
                        {announcement.isPinned && (
                          <span style={{ color: '#FFD700', fontSize: '18px' }}>📌</span>
                        )}
                        <span 
                          style={{ 
                            background: typeInfo.color, 
                            color: 'white', 
                            padding: '4px 12px', 
                            borderRadius: '20px', 
                            fontSize: isMobile ? '10px' : '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          {typeInfo.label}
                        </span>
                        <span 
                          style={{ 
                            background: priorityInfo.color, 
                            color: 'white', 
                            padding: '4px 12px', 
                            borderRadius: '20px', 
                            fontSize: isMobile ? '10px' : '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          {priorityInfo.label}
                        </span>
                        {!announcement.readByCurrentUser && (
                          <span style={{
                            background: '#ff6b6b',
                            color: 'white',
                            padding: '3px 8px',
                            borderRadius: '12px',
                            fontSize: isMobile ? '9px' : '10px',
                            fontWeight: 'bold',
                            animation: 'pulse 2s infinite'
                          }}>
                            {t('messages.new')}
                          </span>
                        )}
                      </div>
                      
                      <h3 style={{ 
                        color: 'white', 
                        margin: '0 0 8px 0',
                        fontSize: isMobile ? '16px' : '20px',
                        fontWeight: 'bold',
                        textAlign: isMobile ? 'center' : 'left'
                      }}>
                        {announcement.title}
                      </h3>
                      
                      <div style={{ 
                        color: '#9ca3af', 
                        fontSize: isMobile ? '11px' : '13px', 
                        lineHeight: '1.4',
                        textAlign: isMobile ? 'center' : 'left'
                      }}>
                        📝 {t('messages.by')} <strong>{announcement.author.fullName}</strong> • 
                        📅 {new Date(announcement.createdAt).toLocaleDateString(navigator.language || 'fr-FR', {
                          day: 'numeric',
                          month: 'long',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {announcement.validUntil && (
                          <span> • ⏰ {t('messages.valid_until_label')} {new Date(announcement.validUntil).toLocaleDateString(navigator.language || 'fr-FR')}</span>
                        )}
                      </div>
                    </div>
                    
                    {canManageAnnouncements && (
                      <div style={{ 
                        display: 'flex', 
                        gap: '8px',
                        flexDirection: isMobile ? 'row' : 'row',
                        justifyContent: isMobile ? 'center' : 'flex-end'
                      }}>
                        <button
                          onClick={() => openEditModal(announcement)}
                          style={{
                            background: '#2196F3',
                            color: 'white',
                            border: 'none',
                            padding: isMobile ? '8px 10px' : '8px 12px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: isMobile ? '10px' : '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          ✏️ {isMobile ? '' : t('messages.edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(announcement.id)}
                          style={{
                            background: '#f44336',
                            color: 'white',
                            border: 'none',
                            padding: isMobile ? '8px 10px' : '8px 12px',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: isMobile ? '10px' : '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          🗑️ {isMobile ? '' : t('messages.delete')}
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Contenu responsive */}
                  <div style={{ 
                    color: 'white', 
                    marginBottom: '20px', 
                    lineHeight: '1.6',
                    fontSize: isMobile ? '13px' : '15px',
                    background: 'rgba(0,0,0,0.2)',
                    padding: isMobile ? '12px' : '15px',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.1)',
                    textAlign: isMobile ? 'justify' : 'left'
                  }}>
                    {announcement.content}
                  </div>

                  {/* Pied responsive */}
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row',
                    justifyContent: 'space-between', 
                    alignItems: isMobile ? 'center' : 'center',
                    gap: isMobile ? '12px' : '0'
                  }}>
                    <div style={{ 
                      color: '#9ca3af', 
                      fontSize: isMobile ? '11px' : '13px', 
                      display: 'flex', 
                      flexDirection: isMobile ? 'column' : 'row',
                      alignItems: 'center', 
                      gap: isMobile ? '8px' : '15px',
                      textAlign: 'center'
                    }}>
                      <span>📊 {t('messages.read_by')} {announcement.readCount}/{announcement.totalRecipients} {t('messages.people')}</span>
                      <span style={{
                        background: announcement.readCount / announcement.totalRecipients > 0.7 ? '#4CAF50' : '#FF9800',
                        color: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: isMobile ? '10px' : '11px'
                      }}>
                        {Math.round(announcement.readCount / announcement.totalRecipients * 100)}{t('messages.read_percent')}
                      </span>
                    </div>
                    
                    {!announcement.readByCurrentUser && (
                      <button
                        onClick={() => handleMarkAsRead(announcement.id)}
                        style={{
                          background: '#4CAF50',
                          color: 'white',
                          border: 'none',
                          padding: isMobile ? '10px 16px' : '8px 16px',
                          borderRadius: '20px',
                          cursor: 'pointer',
                          fontSize: isMobile ? '11px' : '12px',
                          fontWeight: 'bold',
                          width: isMobile ? '100%' : 'auto',
                          maxWidth: isMobile ? '200px' : 'none'
                        }}
                      >
                        ✅ {t('messages.mark_as_read')}
                      </button>
                    )}
                  </div>
                </div>
              );
            })
        )}
      </div>

      {/* Modal de création/modification responsive */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: isMobile ? '10px' : '20px'
        }}>
          <div style={{
            background: '#2c3e50',
            borderRadius: '20px',
            padding: isMobile ? '20px' : '30px',
            maxWidth: isMobile ? '100%' : '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3 style={{ 
              color: 'white', 
              marginBottom: '25px', 
              fontSize: isMobile ? '18px' : '24px',
              textAlign: isMobile ? 'center' : 'left'
            }}>
              {editingAnnouncement ? `✏️ ${t('messages.edit_announcement')}` : `➕ ${t('messages.new_announcement')}`}
            </h3>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: 'white', 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold',
                fontSize: isMobile ? '13px' : '14px'
              }}>
                {t('messages.title_field')} *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                placeholder={t('messages.title_placeholder')}
                style={{
                  width: '100%',
                  padding: isMobile ? '10px' : '12px',
                  borderRadius: '8px',
                  border: 'none',
                  fontSize: isMobile ? '16px' : '16px', // 16px pour éviter zoom iOS
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', 
              gap: '20px', 
              marginBottom: '20px' 
            }}>
              <div>
                <label style={{ 
                  color: 'white', 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  fontSize: isMobile ? '13px' : '14px'
                }}>
                  {t('messages.type')}
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({...formData, type: e.target.value})}
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px' : '12px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: isMobile ? '16px' : '14px',
                    boxSizing: 'border-box'
                  }}
                >
                  {announcementTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ 
                  color: 'white', 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  fontSize: isMobile ? '13px' : '14px'
                }}>
                  {t('messages.priority')}
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => setFormData({...formData, priority: e.target.value})}
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px' : '12px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: isMobile ? '16px' : '14px',
                    boxSizing: 'border-box'
                  }}
                >
                  {priorityOptions.map(priority => (
                    <option key={priority.value} value={priority.value}>
                      {priority.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ 
                  color: 'white', 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  fontSize: isMobile ? '13px' : '14px'
                }}>
                  {t('messages.target_audience')}
                </label>
                <select
                  value={formData.targetAudience}
                  onChange={(e) => setFormData({...formData, targetAudience: e.target.value})}
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px' : '12px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: isMobile ? '16px' : '14px',
                    boxSizing: 'border-box'
                  }}
                >
                  <option value="all">{t('messages.audience_all')}</option>
                  <option value="employees">{t('messages.audience_employees')}</option>
                  <option value="managers">{t('messages.audience_managers')}</option>
                  <option value="admins">{t('messages.audience_admins')}</option>
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                color: 'white', 
                display: 'block', 
                marginBottom: '8px', 
                fontWeight: 'bold',
                fontSize: isMobile ? '13px' : '14px'
              }}>
                {t('messages.content')} *
              </label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({...formData, content: e.target.value})}
                rows={isMobile ? "5" : "6"}
                placeholder={t('messages.content_placeholder')}
                style={{
                  width: '100%',
                  padding: isMobile ? '10px' : '12px',
                  borderRadius: '8px',
                  border: 'none',
                  resize: 'vertical',
                  fontSize: isMobile ? '16px' : '14px',
                  lineHeight: '1.5',
                  boxSizing: 'border-box'
                }}
              />
            </div>

            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', 
              gap: '20px', 
              marginBottom: '25px' 
            }}>
              <div>
                <label style={{
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: isMobile ? '13px' : '14px'
                }}>
                  <input
                    type="checkbox"
                    checked={formData.isPinned}
                    onChange={(e) => setFormData({...formData, isPinned: e.target.checked})}
                    style={{ marginRight: '10px', transform: 'scale(1.2)' }}
                  />
                  📌 {t('messages.pin')}
                </label>
              </div>

              <div>
                <label style={{ 
                  color: 'white', 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: 'bold',
                  fontSize: isMobile ? '13px' : '14px'
                }}>
                  {t('messages.valid_until')}
                </label>
                <input
                  type="date"
                  value={formData.validUntil}
                  onChange={(e) => setFormData({...formData, validUntil: e.target.value})}
                  style={{
                    width: '100%',
                    padding: isMobile ? '10px' : '12px',
                    borderRadius: '8px',
                    border: 'none',
                    fontSize: isMobile ? '16px' : '14px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <div style={{ 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              gap: '15px', 
              justifyContent: 'flex-end' 
            }}>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingAnnouncement(null);
                  resetForm();
                }}
                style={{
                  padding: '12px 24px',
                  background: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: 'bold',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                {t('messages.cancel')}
              </button>
              <button
                onClick={handleSave}
                disabled={!formData.title || !formData.content || loading}
                style={{
                  padding: '12px 24px',
                  background: (formData.title && formData.content && !loading) ? '#4CAF50' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: (formData.title && formData.content && !loading) ? 'pointer' : 'not-allowed',
                  fontSize: isMobile ? '13px' : '14px',
                  fontWeight: 'bold',
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                {loading ? `⏳ ${t('messages.save')}` : editingAnnouncement ? `💾 ${t('messages.edit')}` : `🚀 ${t('messages.publish')}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes slideInFromRight {
          from { 
            transform: translateX(100%); 
            opacity: 0; 
          }
          to { 
            transform: translateX(0); 
            opacity: 1; 
          }
        }
        
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(255, 107, 107, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(255, 107, 107, 0); }
          100% { box-shadow: 0 0 0 0 rgba(255, 107, 107, 0); }
        }
      `}</style>
      </div>

      {/* CSS pour animations */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @media (min-width: 768px) {
          .announcements-content {
            padding: 160px 40px 40px !important;
          }
        }
        @media (min-width: 1024px) {
          .announcements-content {
            padding: 180px 60px 60px !important;
          }
        }
        .announcements-content {
          animation: slideUp 0.4s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AnnouncementsManager;
export { UnreadAnnouncementsWidget };